import { Icon } from '../../components/icons/Icon'
import { Popover } from './Popover'
import type { ColumnDef, SortSpec } from './types'

export interface SortMenuProps<T> {
  columns: ColumnDef<T>[]
  sort: SortSpec[]
  onSortChange: (next: SortSpec[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Multi-column sort: click cycles a column none → ascending → descending →
 * none; a column already active shows its level badge when more than one
 * column is sorted (design-system.md §3.2 ColumnManager "shift-click = add
 * level" — expressed here as click-to-cycle since every column gets its own
 * row rather than living on the grid header only).
 */
export function SortMenu<T>({ columns, sort, onSortChange, open, onOpenChange }: SortMenuProps<T>) {
  function cycle(columnId: string) {
    const existingIndex = sort.findIndex((s) => s.columnId === columnId)
    if (existingIndex === -1) {
      onSortChange([...sort, { columnId, direction: 'asc' }])
      return
    }
    const existing = sort[existingIndex]!
    if (existing.direction === 'asc') {
      const next = [...sort]
      next[existingIndex] = { columnId, direction: 'desc' }
      onSortChange(next)
      return
    }
    onSortChange(sort.filter((s) => s.columnId !== columnId))
  }

  return (
    <Popover
      label="Sort"
      open={open}
      onOpenChange={onOpenChange}
      badge={sort.length || undefined}
      triggerContent={
        <>
          <Icon name="chevrons-up-down" size={14} /> Sort
        </>
      }
    >
      <p style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        Click a column to sort; click again to reverse; a third click clears it. Order below is sort priority.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {columns.map((column) => {
          const level = sort.findIndex((s) => s.columnId === column.id)
          const active = level >= 0 ? sort[level] : undefined
          const disabled = Boolean(column.disabledReason) || !column.sortable
          return (
            <li key={column.id}>
              <button
                type="button"
                className="iw-btn iw-btn--ghost iw-btn--sm"
                style={{ width: '100%', justifyContent: 'space-between' }}
                disabled={disabled}
                title={column.disabledReason}
                aria-pressed={Boolean(active)}
                onClick={() => cycle(column.id)}
              >
                <span>{column.header}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {level >= 0 && sort.length > 1 ? (
                    <span className="iw-pill iw-pill--neutral">{level + 1}</span>
                  ) : null}
                  {active ? (
                    <Icon name={active.direction === 'asc' ? 'chevron-up' : 'chevron-down'} size={14} />
                  ) : null}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </Popover>
  )
}
